import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import {
  parseTableStateFromSearchString,
  buildTableStateSearchParams,
  DEFAULT_PER_PAGE,
} from "@/lib/table-url-state.js";

/**
 * @typedef {Object} TableUrlStateOptions
 * @property {string} basePath - Path the page lives at (e.g. "/no-data-report").
 * @property {Set<string>} [validSortColumns] - If provided, only accept sort column when in this set.
 * @property {string|null} [defaultSortColumn] - Initial sort column when URL has none.
 * @property {'asc'|'desc'} [defaultSortDirection] - Initial sort direction when URL has none.
 * @property {number} [defaultPerPage] - Default rows per page (URL is omitted at this value).
 * @property {string[]} [preserveParams] - Existing URL params to keep when the hook rewrites the URL (e.g. "tab").
 */

/**
 * Bidirectional sync between table state (page / perPage / sort) and the URL.
 *
 * The URL is the source of truth on mount and whenever the user navigates
 * back to the page (e.g. via the asset page's back button), so pagination
 * and sort always restore exactly. State changes from the page (sort, page,
 * rows-per-page) write back to the URL with `replace`.
 *
 * Designed to be reused across any list page that wants its table state
 * preserved across asset round-trips. Pair with `buildAssetLinkSearch` from
 * `@/lib/table-url-state.js` for row-click links and `buildBackToSourcePath`
 * for the back button on the asset page.
 *
 * @param {TableUrlStateOptions} options
 */
export function useTableUrlState({
  basePath,
  validSortColumns,
  defaultSortColumn = null,
  defaultSortDirection = "asc",
  defaultPerPage = DEFAULT_PER_PAGE,
  preserveParams = [],
}) {
  const [, setLocation] = useLocation();
  const locationSearch = useSearch();

  const initial = parseTableStateFromSearchString(
    locationSearch,
    validSortColumns
  );

  const [currentPage, setCurrentPage] = useState(initial.page);
  const [itemsPerPage, setItemsPerPage] = useState(initial.itemsPerPage);
  const [sortColumn, setSortColumn] = useState(
    initial.sortColumn ?? defaultSortColumn
  );
  const [sortDirection, setSortDirection] = useState(
    initial.sortColumn ? initial.sortDirection : defaultSortDirection
  );

  const userHasChangedRef = useRef(false);

  useEffect(() => {
    const parsed = parseTableStateFromSearchString(
      locationSearch,
      validSortColumns
    );
    setCurrentPage(parsed.page);
    setItemsPerPage(parsed.itemsPerPage);
    setSortColumn(parsed.sortColumn ?? defaultSortColumn);
    setSortDirection(
      parsed.sortColumn ? parsed.sortDirection : defaultSortDirection
    );
  }, [locationSearch]);

  useEffect(() => {
    const urlSearch = window.location.search;
    const out = new URLSearchParams(
      urlSearch?.startsWith("?") ? urlSearch.slice(1) : urlSearch || ""
    );

    for (const key of Array.from(out.keys())) {
      if (
        key === "page"
        || key === "perPage"
        || key === "sort"
        || key === "dir"
      ) {
        out.delete(key);
      }
    }

    if (preserveParams.length === 0) {
      for (const key of Array.from(out.keys())) {
        out.delete(key);
      }
    } else {
      for (const key of Array.from(out.keys())) {
        if (!preserveParams.includes(key)) out.delete(key);
      }
    }

    const tableParams = buildTableStateSearchParams(
      { page: currentPage, itemsPerPage, sortColumn, sortDirection },
      { defaultPerPage }
    );
    for (const [k, v] of tableParams) out.set(k, v);

    const query = out.toString();
    const target = query ? `${basePath}?${query}` : basePath;
    const current = window.location.pathname + (window.location.search || "");
    if (current === target) return;
    setLocation(target, { replace: true });
  }, [currentPage, itemsPerPage, sortColumn, sortDirection]);

  const handleSort = (column) => {
    userHasChangedRef.current = true;
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handlePageChange = (page) => {
    userHasChangedRef.current = true;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    userHasChangedRef.current = true;
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    setCurrentPage: handlePageChange,
    itemsPerPage,
    setItemsPerPage: handleItemsPerPageChange,
    sortColumn,
    setSortColumn,
    sortDirection,
    setSortDirection,
    handleSort,
    /** Imperatively reset to page 1 (e.g. when filters change). */
    resetToFirstPage,
  };
}
